import React, { useMemo, useState, useEffect } from 'react';
import styled from 'styled-components';
import { videoService } from '../services/api';

interface DatabaseVideo {
  id: string | number;
  title: string;
  description: string;
  url: string;
  thumbnail?: string;
  duration?: string;
  category: string;
  date_publication: string;
  heure_publication: string;
  created_at: string;
  updated_at: string;
}

type VideoItem = DatabaseVideo;

// URL de base de l'API (backend)
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://aikash.ci';

// Construit une URL complète pour les médias stockés côté backend (/uploads/...)
const buildMediaUrl = (url?: string): string => {
  if (!url) return '';
  // Déjà absolu
  if (/^https?:\/\//i.test(url)) return url;
  // Fichiers uploadés sur le backend
  if (url.startsWith('/uploads/')) return encodeURI(`${API_BASE_URL}${url}`);
  // Sinon, ressource statique du frontend
  return url;
};

// Helpers
const formatDurationFromSeconds = (seconds?: number): string => {
  if (!seconds || !Number.isFinite(seconds) || seconds <= 0) return '00:00';
  const s = Math.floor(seconds % 60);
  const m = Math.floor((seconds / 60) % 60);
  const h = Math.floor(seconds / 3600);
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
};

const getVideoUrl = (video: VideoItem): string => buildMediaUrl(video.url);

// Composants stylisés
const MainContent = styled.main`
  flex: 1;
  margin-top: 80px;
  min-height: calc(100vh - 80px);
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, rgba(30, 64, 175, 0.8) 0%, rgba(59, 130, 246, 0.8) 100%);
  color: white;
  padding: 80px 20px;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 20px;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const VideosSection = styled.section`
  padding: 80px 0;
  background: white;
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  color: #333;
  margin-bottom: 50px;
  font-weight: 700;
`;

const VideosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 40px;
  margin-top: 50px;
`;

const VideoCard = styled.div<{ isNew?: boolean }>`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0,0,0,0.1);
  transition: transform 0.3s ease;
  position: relative;
  
  ${props => props.isNew && `
    border: 3px solid #10b981;
    &::before {
      content: '🆕 Nouveau';
      position: absolute;
      top: 15px;
      right: 15px;
      background: #10b981;
      color: white;
      padding: 5px 10px;
      border-radius: 15px;
      font-size: 0.8rem;
      font-weight: 600;
      z-index: 3;
    }
  `}
  
  &:hover {
    transform: translateY(-10px);
  }
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  height: 250px;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const VideoThumbnailImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
`;

const PlayButton = styled.button`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  font-size: 2rem;
  color: #1e40af;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(0,0,0,0.2);

  &:hover {
    background: white;
    transform: translate(-50%, -50%) scale(1.1);
    box-shadow: 0 6px 30px rgba(0,0,0,0.3);
  }
`;

const VideoContent = styled.div`
  padding: 25px;
`;

const VideoTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 10px;
  line-height: 1.4;
`;

const VideoDescription = styled.p`
  color: #6b7280;
  line-height: 1.6;
  margin-bottom: 20px;
  font-size: 0.95rem;
`;

const VideoMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.9rem;
  color: #9ca3af;
`;

const VideoDuration = styled.span`
  background: #f3f4f6;
  padding: 4px 8px;
  border-radius: 6px;
  font-weight: 600;
`;

const VideoDate = styled.span`
  font-weight: 500;
`;

const CategoryFilter = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-bottom: 40px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 10px;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 50px;
  font-size: 1.2rem;
  color: #6b7280;
`;

const LoadingText = styled.div`
  margin-left: 10px;
  animation: pulse 2s infinite;
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const FilterButton = styled.button<{ active: boolean }>`
  padding: 12px 24px;
  border: 2px solid ${props => props.active ? '#1e40af' : '#e5e7eb'};
  background: ${props => props.active ? '#1e40af' : 'white'};
  color: ${props => props.active ? 'white' : '#374151'};
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.95rem;

  &:hover {
    background: ${props => props.active ? '#1e40af' : '#f3f4f6'};
    border-color: ${props => props.active ? '#1e40af' : '#d1d5db'};
  }

  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 0.9rem;
  }
`;

const PaginationRow = styled.div`
  margin-top: 40px;
  display: flex;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const PageButton = styled.button<{ active?: boolean }>`
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid ${p => (p.active ? '#1e40af' : '#e5e7eb')};
  background: ${p => (p.active ? '#1e40af' : '#fff')};
  color: ${p => (p.active ? '#fff' : '#111827')};
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 44px;

  &:hover {
    background: ${p => (p.active ? '#1e40af' : '#f3f4f6')};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

function VideosPage() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [allVideos, setAllVideos] = useState<VideoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState<{
    title: string;
    description?: string;
    url: string;
  } | null>(null);
  const [videoMeta, setVideoMeta] = useState<Record<string, { durationSeconds?: number; thumbDataUrl?: string }>>({});
  const [currentPage, setCurrentPage] = useState(1);

  const pageSize = 6;

  // Charger les vidéos depuis la base de données au montage du composant
  useEffect(() => {
    const loadVideos = async () => {
      try {
        setLoading(true);
        const dbVideos = await videoService.getAllVideos();
        setAllVideos(dbVideos || []);
      } catch (error) {
        console.error('Erreur lors du chargement des vidéos:', error);
        setAllVideos([]);
      } finally {
        setLoading(false);
      }
    };

    loadVideos();
  }, []);

  const filteredVideos = useMemo(() => {
    const list = selectedCategory === 'all'
      ? allVideos
      : allVideos.filter(video => video.category === selectedCategory);
    return list;
  }, [allVideos, selectedCategory]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory]);

  // Générer thumbnail + durée réelles (sans dépendre d'ffmpeg côté serveur)
  useEffect(() => {
    let cancelled = false;

    const getCacheKey = (kind: 'thumb' | 'dur', id: string) => `shotokan_video_${kind}_${id}`;

    const loadMetaForVideo = async (v: DatabaseVideo) => {
      const url = buildMediaUrl(v.url);
      if (!url) return;

      // Duration cache
      const cachedDur = localStorage.getItem(getCacheKey('dur', String(v.id)));
      const cachedThumb = localStorage.getItem(getCacheKey('thumb', String(v.id)));

      if (cachedDur || cachedThumb) {
        setVideoMeta(prev => ({
          ...prev,
          [String(v.id)]: {
            durationSeconds: cachedDur ? Number(cachedDur) : prev[String(v.id)]?.durationSeconds,
            thumbDataUrl: cachedThumb || prev[String(v.id)]?.thumbDataUrl
          }
        }));
      }

      // Already loaded
      if (cachedDur && cachedThumb) return;

      const el = document.createElement('video');
      el.crossOrigin = 'anonymous';
      el.preload = 'metadata';
      el.muted = true;
      el.playsInline = true;
      el.src = url;

      const waitEvent = (name: string) =>
        new Promise<void>((resolve, reject) => {
          const onOk = () => {
            cleanup();
            resolve();
          };
          const onErr = () => {
            cleanup();
            reject(new Error(`Video event error: ${name}`));
          };
          const cleanup = () => {
            el.removeEventListener(name, onOk);
            el.removeEventListener('error', onErr);
          };
          el.addEventListener(name, onOk, { once: true });
          el.addEventListener('error', onErr, { once: true });
        });

      try {
        if (!cachedDur) {
          await waitEvent('loadedmetadata');
          const dur = Number(el.duration);
          if (Number.isFinite(dur) && dur > 0) {
            localStorage.setItem(getCacheKey('dur', String(v.id)), String(dur));
            if (!cancelled) {
              setVideoMeta(prev => ({
                ...prev,
                [String(v.id)]: { ...prev[String(v.id)], durationSeconds: dur }
              }));
            }
          }
        }

        if (!cachedThumb) {
          // Seek to 1s (or 0s if shorter)
          const target = Number.isFinite(el.duration) && el.duration > 1 ? 1 : 0;
          // iOS/Safari: sometimes need metadata before seeking
          if (el.readyState < 1) {
            await waitEvent('loadedmetadata');
          }
          el.currentTime = target;
          await waitEvent('seeked');

          const canvas = document.createElement('canvas');
          const w = el.videoWidth || 480;
          const h = el.videoHeight || 270;
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(el, 0, 0, w, h);
            const dataUrl = canvas.toDataURL('image/jpeg', 0.75);
            localStorage.setItem(getCacheKey('thumb', String(v.id)), dataUrl);
            if (!cancelled) {
              setVideoMeta(prev => ({
                ...prev,
                [String(v.id)]: { ...prev[String(v.id)], thumbDataUrl: dataUrl }
              }));
            }
          }
        }
      } catch (e) {
        // Pas bloquant: on garde le thumbnail existant (SVG fallback côté backend)
        // console.warn('Meta load failed for video', v.id, e);
      } finally {
        el.removeAttribute('src');
        try { el.load(); } catch {}
      }
    };

    const run = async () => {
      const db = filteredVideos as DatabaseVideo[];
      // limiter: générer la meta uniquement pour les vidéos visibles (page courante)
      const start = (currentPage - 1) * pageSize;
      const end = start + pageSize;
      const visible = db.slice(start, end);

      // Séquentiel pour éviter de saturer le réseau/CPU
      for (const v of visible) {
        if (cancelled) break;
        // eslint-disable-next-line no-await-in-loop
        await loadMetaForVideo(v);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [filteredVideos, currentPage]);

  const handleVideoClick = (video: VideoItem) => {
    const url = buildMediaUrl(video.url);
    if (!url) return;
    // Lire sur la même page (modal) au lieu d'ouvrir le fichier mp4 dans un nouvel onglet (souvent téléchargé)
    setActiveVideo({
      title: video.title,
      description: video.description,
      url
    });
  };

  const getVideoDate = (video: VideoItem): string => {
    return new Date(video.date_publication).toLocaleDateString('fr-FR');
  };

  const getVideoDuration = (video: VideoItem): string => {
    const meta = videoMeta[String(video.id)];
    if (meta?.durationSeconds) return formatDurationFromSeconds(meta.durationSeconds);
    return video.duration || '00:00';
  };

  const getVideoThumbnail = (video: VideoItem): string => {
    const meta = videoMeta[String(video.id)];
    if (meta?.thumbDataUrl) return meta.thumbDataUrl;
    return buildMediaUrl(video.thumbnail) || '/karate1.webp';
  };

  const categories = useMemo(() => {
    const unique = Array.from(new Set(allVideos.map(v => v.category).filter(Boolean)));
    return [{ id: 'all', name: 'Toutes les vidéos' }, ...unique.map(c => ({ id: c, name: c }))];
  }, [allVideos]);

  const totalPages = Math.max(1, Math.ceil(filteredVideos.length / pageSize));
  const paginatedVideos = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredVideos.slice(start, start + pageSize);
  }, [filteredVideos, currentPage]);

  const goToPage = (p: number) => {
    const next = Math.min(Math.max(1, p), totalPages);
    setCurrentPage(next);
  };

  return (
    <MainContent>
      <HeroSection>
        <Container>
          <HeroTitle>🎥 Vidéos Karaté Shotokan</HeroTitle>
          <HeroSubtitle>
            Découvrez notre collection de vidéos : compétitions, formations, 
            démonstrations et documentaires sur le karaté traditionnel
          </HeroSubtitle>
        </Container>
      </HeroSection>

      <VideosSection>
        <Container>
          <SectionTitle>Notre Collection Vidéo</SectionTitle>
          
          <CategoryFilter>
            {categories.map(category => (
              <FilterButton
                key={category.id}
                active={selectedCategory === category.id}
                onClick={() => setSelectedCategory(category.id)}
              >
                {category.name}
              </FilterButton>
            ))}
          </CategoryFilter>

          {loading ? (
            <LoadingSpinner>
              <div>⏳</div>
              <LoadingText>Chargement des vidéos...</LoadingText>
            </LoadingSpinner>
          ) : (
            <VideosGrid>
              {paginatedVideos.map(video => (
                <VideoCard key={video.id} isNew={true}>
                  <VideoContainer>
                    <VideoThumbnailImg
                      src={getVideoThumbnail(video) || '/karate1.webp'}
                      alt={video.title}
                      loading="lazy"
                      onError={(e) => {
                        const img = e.currentTarget;
                        // éviter une boucle si le fallback échoue aussi
                        img.onerror = null;
                        img.src = '/karate1.webp';
                      }}
                    />
                    <PlayButton onClick={() => handleVideoClick(video)}>
                      ▶
                    </PlayButton>
                  </VideoContainer>
                  <VideoContent>
                    <VideoTitle>{video.title}</VideoTitle>
                    <VideoDescription>{video.description}</VideoDescription>
                    <VideoMeta>
                      <VideoDuration>{getVideoDuration(video)}</VideoDuration>
                      <VideoDate>{getVideoDate(video)}</VideoDate>
                    </VideoMeta>
                  </VideoContent>
                </VideoCard>
              ))}
            </VideosGrid>
          )}

          {!loading && filteredVideos.length > pageSize && (
            <PaginationRow>
              <PageButton disabled={currentPage <= 1} onClick={() => goToPage(currentPage - 1)}>
                ‹
              </PageButton>
              {Array.from({ length: totalPages }).slice(0, 10).map((_, idx) => {
                const page = idx + 1;
                return (
                  <PageButton
                    key={page}
                    active={page === currentPage}
                    onClick={() => goToPage(page)}
                  >
                    {page}
                  </PageButton>
                );
              })}
              <PageButton disabled={currentPage >= totalPages} onClick={() => goToPage(currentPage + 1)}>
                ›
              </PageButton>
            </PaginationRow>
          )}
        </Container>
      </VideosSection>

      {/* Modal lecteur vidéo */}
      {activeVideo && (
        <VideoModalOverlay
          role="dialog"
          aria-modal="true"
          onClick={() => setActiveVideo(null)}
        >
          <VideoModalContent onClick={(e) => e.stopPropagation()}>
            <VideoModalHeader>
              <div>
                <VideoModalTitle>{activeVideo.title}</VideoModalTitle>
                {activeVideo.description && (
                  <VideoModalSubtitle>{activeVideo.description}</VideoModalSubtitle>
                )}
              </div>
              <VideoModalCloseButton onClick={() => setActiveVideo(null)} aria-label="Fermer">
                ✕
              </VideoModalCloseButton>
            </VideoModalHeader>

            <VideoPlayer
              src={activeVideo.url}
              controls
              autoPlay
              playsInline
              preload="metadata"
              controlsList="nodownload"
            />
          </VideoModalContent>
        </VideoModalOverlay>
      )}
    </MainContent>
  );
}

export default VideosPage;

// Modal styles (à la fin pour garder le fichier lisible)
const VideoModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 9999;
`;

const VideoModalContent = styled.div`
  width: min(980px, 100%);
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 70px rgba(0,0,0,0.35);
`;

const VideoModalHeader = styled.div`
  display: flex;
  gap: 16px;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px 18px;
  border-bottom: 1px solid #e5e7eb;
`;

const VideoModalTitle = styled.div`
  font-weight: 800;
  color: #111827;
  font-size: 1.05rem;
  line-height: 1.3;
`;

const VideoModalSubtitle = styled.div`
  margin-top: 4px;
  color: #6b7280;
  font-size: 0.9rem;
  line-height: 1.4;
  max-width: 780px;
`;

const VideoModalCloseButton = styled.button`
  border: none;
  background: #f3f4f6;
  color: #111827;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 1.1rem;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;

  &:hover {
    background: #e5e7eb;
  }
`;

const VideoPlayer = styled.video`
  width: 100%;
  height: auto;
  max-height: calc(100vh - 180px);
  background: #000;
`;
